/**
 * 复制文本
 * @param options
 */

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: any) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
      getResponse: (widgetId?: string) => string
      execute: (container?: string | HTMLElement, options?: any) => void
    }
    Android?: {
      onTurnstileSuccess: (token: string) => void
      // Allow dynamic method names for bridge handlers
      [key: string]: ((...args: any[]) => void) | undefined
    }
  }
}

export async function copy(text: string): Promise<boolean> {
  const isSupportClipboard = navigator && 'clipboard' in navigator
  try {
    if (isSupportClipboard) {
      await navigator!.clipboard.writeText(text)
      return true
    } else {
      // 兼容性方案
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // 防止滚动
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const success = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (!success) {
        return false
      }
      return true
    }
  } catch (e) {
    console.error('Copy failed:', e)
    return false
  }
}

export function isMemeShotApp() {
  const userAgent = navigator.userAgent.toLowerCase()
  const d = /(memeshotapp)/i
  return d.test(userAgent)
}

export function isAndroid() {
  const userAgent = navigator.userAgent
  return userAgent.indexOf('Android') > -1 || userAgent.indexOf('Adr') > -1 // android终端
}
export function isIOS() {
  const ua = navigator.userAgent
  return (
    /\(i[^;]+;( U;)? CPU.+Mac OS X/.test(ua) || // 旧式 iOS UA
    /iPhone|iPad|iPod/i.test(ua) ||              // 新型或自定义 UA
    ua.includes('iOS') ||                        // 新增：APP自定义UA标识
    (ua.includes('Mac') && 'ontouchend' in document) // iPadOS Safari
  )
}

export function formatNumber(value: any, decimals = 2, showUnit = true) {
  if (value === null || value === undefined) return '-'
  const units = ['', 'K', 'M', 'B', 'T']
  let unitIndex = 0
  let scaledValue: any = Math.abs(value)
  while (scaledValue >= 1000 && unitIndex < units.length - 1) {
    unitIndex += 1
    scaledValue /= 1000
  }

  // 处理小数位数

  scaledValue = scaledValue.toFixed(decimals)

  // 去除末尾多余的0和小数点

  scaledValue = parseFloat(scaledValue)

  return value < 0
    ? `-${scaledValue}${showUnit ? units[unitIndex] : ''}`
    : `${scaledValue}${showUnit ? units[unitIndex] : ''}`
}

export function formatMarketCap(value: any) {
  if (!value) return '- -'

  return `$${formatNumber(value)}`
}

/**
 * 获取代币地址
 * @param {string} str
 * @returns {any}
 */
export function formatAddress(str: string, startWidth = 5, endWidth = 5) {
  if (str && str.length > startWidth + endWidth) {
    const reg = new RegExp(
      '^(\\S{' + startWidth + '})(.*)(\\S{' + endWidth + '})$',
    )
    return str.replace(reg, '$1...$3')
  }
  return str
}

/**
 * @description 千分位以逗号隔开
 * @param {number | null} n - 待格式化的数字
 * @returns {string} - 格式化后的字符串或 "--"
 */
export function formatNumberWithCommas(n: number | null): string {
  // 如果n为null，返回"--"
  if (n === null || n === undefined) {
    return '--'
  }

  const num: string = (n || 0).toString()
  let number: number = 0,
    floatNum: string = '',
    intNum: any = []

  // 判断是否有小数位，有则截取小数点后的数字
  if (num.indexOf('.') > 0) {
    number = num.indexOf('.') // 获取小数点出现的位置
    floatNum = num.substr(number) // 截取小数点后的部分
    intNum = num.substring(0, number) // 截取小数点前的部分
  } else {
    intNum = num
  }

  // eslint-disable-next-line prefer-const
  let result: string[] = [],
    counter: number = 0

  intNum = intNum.split('')

  // 利用3的倍数，向数组插入','
  for (let i = intNum.length - 1; i >= 0; i--) {
    counter++
    result.unshift(intNum[i])
    if (!(counter % 3) && i != 0) {
      result.unshift(',')
    }
  }

  return result.join('') + floatNum || ''
}

/**
 * @description 将数字转换为百分比格式
 * @param {number | null} n - 待转换的数字
 * @returns {string} - 百分比字符串或 "--"
 */
export function formatPercentage(n: number | null): string {
  // 如果n为null或undefined，返回"--"
  if (n === null || n === undefined) {
    return '--'
  }
  const percentage: number = n * 100
  return percentage % 1 === 0 ? `${percentage.toFixed(0)}%` : `${percentage}%`
}

/**
 * 转换科学计数法
 */
export function scientificToDecimal(num: number | string, decimals = 8) {
  // 如果输入是字符串，先转换为数字
  if (typeof num === 'string') {
    num = parseFloat(num);
  }

  // 处理非数字情况
  if (isNaN(num)) {
    return 'NaN';
  }

  // 转换为字符串并处理科学计数法
  const str = num.toString();
  if (str.indexOf('e') === -1) {
    // 非科学计数法，也需要按照decimals截取小数位数
    const dotIndex = str.indexOf('.');
    if (dotIndex === -1) {
      return str;
    }
    // 截取小数点后指定位数
    const integerPart = str.substring(0, dotIndex);
    const decimalPart = str.substring(dotIndex + 1, dotIndex + 1 + decimals);
    // 拼接整数部分和小数部分
    const result = integerPart + '.' + decimalPart;
    // 去除尾部多余的零
    return result.replace(/\.?0+$/, '');
  }

  // 使用toFixed处理大数字
  return num.toFixed(decimals).replace(/\.?0+$/, '');
}

/**
 * 截取小数点后几位
 */
export function toFixedDecimal(num: number | string, decimals = 8, miniEffective = 2) {
  // 转换输入为数字
  const numberValue = typeof num === 'string' ? parseFloat(num) : num;
  
  // 处理非数字情况
  if (isNaN(numberValue)) {
    return 'NaN';
  }
  
  // 处理零的情况
  if (numberValue === 0) {
    return '0';
  }
  
  // 对于非常小的数（小于1e-6），特殊处理
  if (Math.abs(numberValue) < 1e-6) {
    // 计算小数点后需要的总位数
    const decimalPlaces = Math.ceil(Math.abs(Math.log10(Math.abs(numberValue)))) + miniEffective;
    // 生成足够精度的字符串表示
    const str = numberValue.toFixed(decimalPlaces);
    
    // 提取小数部分
    const dotIndex = str.indexOf('.');
    if (dotIndex === -1) {
      return str;
    }
    
    const integerPart = str.substring(0, dotIndex);
    let decimalPart = str.substring(dotIndex + 1);
    
    // 找到第一个非零数字的位置
    const firstNonZeroIndex = decimalPart.search(/[1-9]/);
    if (firstNonZeroIndex !== -1) {
      // 确保至少保留miniEffective位有效数字
      const effectiveDecimals = firstNonZeroIndex + miniEffective;
      decimalPart = decimalPart.substring(0, effectiveDecimals);
    }
    
    // 拼接结果
    const result = integerPart + '.' + decimalPart;
    
    // 对于非常小的数，确保显示足够的小数位
    if (result === '0' && Math.abs(numberValue) > 0) {
      // 计算需要的小数位数
      const requiredDecimals = Math.ceil(Math.abs(Math.log10(Math.abs(numberValue)))) + miniEffective;
      // 生成相应的小数格式
      let formattedResult = '0.';
      let foundNonZero = false;
      let nonZeroCount = 0;
      
      // 使用原始的高精度字符串
      const fullDecimalPart = str.substring(dotIndex + 1);
      
      for (let i = 0; i < requiredDecimals; i++) {
        const digit = i < fullDecimalPart.length ? fullDecimalPart[i] : '0';
        formattedResult += digit;
        
        if (digit !== '0' && !foundNonZero) {
          foundNonZero = true;
        }
        
        if (foundNonZero && ++nonZeroCount >= miniEffective) {
          break;
        }
      }
      
      return formattedResult;
    }
    
    return result;
  }
  
  // 处理普通数
  const str = numberValue.toString();
  
  // 检查是否包含小数点
  const dotIndex = str.indexOf('.');
  if (dotIndex === -1) {
    return str;
  }
  
  // 截取小数点后指定位数
  const integerPart = str.substring(0, dotIndex);
  const decimalPart = str.substring(dotIndex + 1, dotIndex + 1 + decimals);
  
  // 拼接结果
  const result = integerPart + '.' + decimalPart;
  
  // 去除尾部多余的零
  return result.replace(/\.?0+$/, '');
}

export function mobileCheck(userAgent: string) {
  let check = false
    ; (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      ) {
        check = true
      }
    })(userAgent)
  return check
}

export function waitForTurnstile(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve()
      return
    }

    const timeout = setTimeout(() => {
      reject(new Error('Turnstile script loading timeout'))
    }, 10000)

    const checkInterval = setInterval(() => {
      if (window.turnstile) {
        clearTimeout(timeout)
        clearInterval(checkInterval)
        resolve()
      }
    }, 100)
  })
}